import { meAtom } from "@/atoms/users"
import { useEffect, useState } from "react"
import { useRecoilValue } from "recoil"
import { cn, getThemeImageUrl } from "@/utils/helpers"
import { useTheme } from "context/theme-context"
import { Input } from "@/core/components/input/Input"
import { PhoneInput } from "@/core/components/phone/PhoneInput"
import { Label } from "@/core/components/Label"
import { useTranslation } from "next-i18next"
import { isPhoneValid } from "@/core/components/phone/PhoneUtil"
import { Button } from "@/core/components/Button"
import { useSignupUser } from "@/modules/users/hooks/useSignupUser"
import { CreateUser } from "@/modules/users/models/createUser"
import { StatusCode } from "@/core/generated/api/enums/status_code"
import { toast } from "react-toastify"
import { useRouter } from "next/router"

const nameRegex = /^[\p{L}\s'.-]+(?: [\p{L}\s'.-]+)+$/u

export function SignUp() {
  const { t } = useTranslation("common")
  const me = useRecoilValue(meAtom)
  const router = useRouter()
  const { returnUrl } = router.query
  const { signUp, loading, code, message } = useSignupUser()
  const [user, setUser] = useState(me)
  const [isLoading, setIsLoading] = useState(false)
  const theme = useTheme()
  const authImg = getThemeImageUrl(theme?.authimg)
  const authLogo = getThemeImageUrl(theme?.authlogo)
  const [validate, setValidate] = useState(false)
  const [fullName, setFullName] = useState(
    (me?.firstname && me?.firstname + " " + me?.lastname) || "",
  )
  const [phoneCountryInformation, setPhoneCountryInformation] = useState<{
    iso2: string
    dialCode: string
  } | null>(null)

  const [showErrors, setShowErrors] = useState<Record<string, string | null>>({
    fullName: null,
    title: null,
    phone: null,
  })

  const validateFields = (): boolean => {
    const titleError = (!user?.title && "Title is required") || null
    const fullNameError =
      (!user.firstname && "Name is required") ||
      (!nameRegex.test(fullName) &&
        "Full name must contain first and last name") ||
      null
    const phoneError =
      (user.phone.length > 3 &&
        phoneCountryInformation?.iso2 &&
        !isPhoneValid(user.phone, phoneCountryInformation.iso2) &&
        "Phone number is invalid") ||
      null

    setShowErrors({
      fullName: fullNameError,
      title: titleError,
      phone: phoneError,
    })

    if (titleError || fullNameError || phoneError) {
      return false
    }
    return true
  }

  const handleClick = async () => {
    setValidate(true)
    if (!validateFields()) {
      return
    }
    setIsLoading(true)
    const args = new CreateUser({
      email: me?.email,
      firstname: user?.firstname,
      lastname: user?.lastname,
      phone: user?.phone,
      title: user?.title,
    })
    const res = await signUp(args)

    if (res?.code !== StatusCode.Ok) {
      toast.error(message || "Failed to signup. Please try again")
      return
    }

    window.location.assign((returnUrl as string) || "/")
  }

  const handleChangeUser = (key) => (e) => {
    if (e?.target) e = e.target.value

    setUser((user) => ({
      ...user,
      [key]: e,
    }))
  }

  const handleFullNameChange = (e) => {
    if (e?.target) e = e.target.value
    setFullName(e)

    const firstname = e.split(" ")[0] || ""
    const lastname = e.split(" ").slice(1).join(" ") || ""

    setUser((user) => ({
      ...user,
      firstname,
      lastname,
    }))
  }

  useEffect(() => {
    if (validate) validateFields()
  }, [user])

  return (
    <div>
      <div
        style={{ backgroundImage: authImg }}
        className="login fixed z-[100] h-full w-full bg-cover px-4 py-16"
      ></div>
      <div className="absolute top-[50%] z-[101] flex w-full translate-y-[-50%]">
        <div
          className={`${
            theme?.login_color ?? theme?.login_screen?.color ?? "text-primary"
          } max-sm:hidden sm:w-7/12 sm:pl-4 sm:pt-40 lg:w-8/12 lg:pl-10 2xl:w-9/12 2xl:pl-60 2xl:pr-60 2xl:pt-80`}
        >
          <p className="font-light sm:text-4xl lg:text-6xl">
            {theme?.login_headline ?? theme?.login_screen?.title}
          </p>
          <p className="pt-8 text-2xl md:pr-20 lg:pr-40">
            {theme?.login_description ?? theme?.login_screen?.statement}
          </p>
        </div>
        <div className="ml-auto items-end justify-center max-sm:ml-4 max-sm:mr-4 max-sm:w-full md:mr-4 md:w-5/12 lg:mr-10 lg:w-4/12 2xl:mr-40 2xl:w-3/12">
          <div className="bg-company-white mb-4 mt-16 w-full rounded-xl p-10 shadow">
            <div
              style={{ backgroundImage: authLogo }}
              className="mx-auto h-16 w-60 bg-contain bg-center bg-no-repeat pb-8"
            ></div>
            <p className="mt-4 py-2 text-center text-2xl font-medium">
              Finish your account
            </p>
            <div className="flex w-full items-center justify-between pt-5">
              <hr className="w-full bg-gray-400" />
              <p className="select-none px-4 text-[10px] font-medium uppercase leading-4 text-gray-400">
                Personal
              </p>
              <hr className="w-full bg-gray-400" />
            </div>
            <div className="mt-4 flex w-full flex-col space-y-4">
              <div className="w-full pl-0.5">
                <Label
                  className={cn("text-on-surface-variant text-md text-left")}
                >
                  Email
                </Label>
                <div className="">{user.email}</div>
              </div>

              <Input
                t={t}
                label="Full name"
                value={fullName}
                onChange={handleFullNameChange}
                className={cn({ "border-red-500": !nameRegex.test(fullName) })}
                error={showErrors.fullName}
              />
              <Input
                t={t}
                label="Title"
                value={user?.title}
                onChange={handleChangeUser("title")}
                error={showErrors.title}
              />
              <div className="mt-4">
                <PhoneInput
                  label="Phone number"
                  value={user?.phone || ""}
                  onChange={(phone, e) => {
                    setUser((prev) => ({
                      ...prev,
                      phone,
                    }))
                    setPhoneCountryInformation(e.country)
                  }}
                  disabled={false}
                  className={""}
                  placeholder={""}
                  error={showErrors.phone}
                  isCompleted={false}
                />
              </div>
            </div>
            <div className="mt-8">
              <Button
                rightIconName="send"
                role="button"
                variant="default"
                className="h-14 w-full py-4 text-xl font-medium"
                onClick={handleClick}
                loading={loading || isLoading}
                type="button"
              >
                Finish signup
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
